import { getMarketQuotes } from "./stablesApi";
import { DISPLAY_MARKET_QUOTE_AMOUNT } from "../Pages/Private/Stables/constants";
import packageJson from "../../package.json";

/** Shared in-flight + TTL cache for GET /v1/stables/markets/quotes (buy/amount/1 BRL unit price). */

export const QUOTES_CACHE_TTL_MS = 15_000;

/** Bust in-memory quotes after each frontend deploy (set REACT_APP_BUILD_ID in CI). */

/** Bump suffix when quote price resolution changes (invalidates stale in-memory entries). */
export const MARKET_QUOTES_CACHE_EPOCH = [
  "foxbit-price-v2",
  process.env.REACT_APP_BUILD_ID?.trim() || packageJson.version,
].join(":");

let quotesCache = null;

let quotesCacheExpiresAt = 0;

/** In-flight requests keyed by full cache key (side/mode/amount/asset). */
const quotesInflightByKey = new Map();

/**
 * Returns cached market quotes or a single shared in-flight promise per cache key.
 * All hooks (balances, carousel, mini chart) must use this — not getMarketQuotes directly.
 */

export const getCachedMarketQuotes = async (
  { side = "buy", mode = "amount", amount = DISPLAY_MARKET_QUOTE_AMOUNT, asset } = {},
  { force = false } = {}
) => {
  const cacheKey = `${MARKET_QUOTES_CACHE_EPOCH}:${side}:${mode}:${amount}:${asset ?? "*"}`;

  const now = Date.now();

  if (!force && quotesCache?.key === cacheKey && now < quotesCacheExpiresAt) {
    return quotesCache.data;
  }

  if (!force && quotesInflightByKey.has(cacheKey)) {
    return quotesInflightByKey.get(cacheKey);
  }

  const inflight = getMarketQuotes({ side, mode, amount, asset })
    .then((data) => {
      quotesCache = { key: cacheKey, data };
      quotesCacheExpiresAt = Date.now() + QUOTES_CACHE_TTL_MS;
      return data;
    })
    .finally(() => {
      quotesInflightByKey.delete(cacheKey);
    });

  quotesInflightByKey.set(cacheKey, inflight);
  return inflight;
};

export const invalidateMarketQuotesCache = () => {
  quotesCache = null;
  quotesCacheExpiresAt = 0;
  quotesInflightByKey.clear();
};
